import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

/**
 * The structural shape every photo type in the app already satisfies -
 * TankDetailsPhoto (both declarations of it) and TankDetailsPhotoDto - so
 * callers pass their own arrays straight in without a cast or a mapping step.
 */
export interface CarouselPhoto {
  url: string;
  thumbnailUrl?: string;
  isPrimary?: boolean;
}

/**
 * Single-slide photo carousel. Owns the keen-slider instance and nothing else:
 * no loading, empty or error state, no toolbar, no data fetching. Consumers
 * supply those, which is what lets the tank-details Photos widget and the map
 * card share one slider instead of keeping a copy each.
 */
@Component({
  selector: 'app-photo-carousel',
  templateUrl: './photo-carousel.component.html',
  styleUrls: ['./photo-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoCarouselComponent implements AfterViewChecked, OnDestroy {
  @Input()
  set photos(value: CarouselPhoto[] | null | undefined) {
    this._photos = value ?? [];
    this.currentSlide = 0;
    // The #sliderRef element survives an input change, so the ViewChild setter
    // below does not re-fire and keen-slider keeps the old slide count. Ask for
    // an update once the new slides are actually in the DOM.
    this._needsSliderUpdate = true;
  }
  get photos(): CarouselPhoto[] {
    return this._photos;
  }

  /** Prefer `thumbnailUrl` for the slide. `photoClick` still emits the whole photo. */
  @Input() useThumbnails = false;

  @Input() showPrimaryBadge = true;

  /** Appended as `?cb=` so a freshly uploaded photo is not served from cache. */
  @Input() cacheBust: number | null = null;

  /**
   * `outside` puts the arrows either side of the image, shrinking the image
   * to make room for them. `overlay` floats the arrows and counter on the
   * image instead, keeping it flush with the container - what both the
   * tank-details Photos widget and the map card use.
   */
  @Input() navPlacement: 'outside' | 'overlay' = 'outside';

  @Output() photoClick = new EventEmitter<CarouselPhoto>();

  currentSlide = 0;

  private _photos: CarouselPhoto[] = [];
  private _slider: KeenSliderInstance | null = null;
  private _needsSliderUpdate = false;

  get slider(): KeenSliderInstance | null {
    return this._slider;
  }

  @ViewChild('sliderRef') set sliderRef(el: ElementRef<HTMLElement> | undefined) {
    this.destroySlider();
    if (!el?.nativeElement) return;

    this._slider = new KeenSlider(el.nativeElement, {
      loop: true,
      slides: { perView: 1 },
      slideChanged: (s) => {
        this.currentSlide = s.track.details.rel;
        this.cdr.markForCheck();
      },
    });
    this.currentSlide = 0;
    this._needsSliderUpdate = false;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  srcFor(photo: CarouselPhoto): string {
    const base = (this.useThumbnails && photo.thumbnailUrl) || photo.url;
    return this.cacheBust === null ? base : `${base}?cb=${this.cacheBust}`;
  }

  ngAfterViewChecked(): void {
    if (!this._needsSliderUpdate) return;
    this._needsSliderUpdate = false;
    this._slider?.update();
  }

  ngOnDestroy(): void {
    this.destroySlider();
  }

  private destroySlider(): void {
    this._slider?.destroy();
    this._slider = null;
  }
}
